import React, { useState, useEffect } from 'react';
import MaicBrand from '../components/MaicBrand';
import {
  Plug, Network, Activity, Workflow, Settings,
  ChevronLeft, ChevronRight, LayoutDashboard, ChevronDown,
  FolderKanban, LogOut, DollarSign, Briefcase,
  BrainCircuit, Bot, MessageSquare, ShieldCheck, Wrench, Globe, FlaskConical,
  Database, Shield, TrendingUp, BookOpen, Clock, Radio, BarChart2, Boxes,
  Sparkles, Compass, Target, Play, GraduationCap, Siren, LifeBuoy, HelpCircle,
  MessagesSquare, FileText, Trash2, GitBranch, GitPullRequest, FolderOpen,
  MousePointerClick, Inbox, ScanSearch, AppWindow, Layers, Home, Search, KeyRound,
  DraftingCompass, Package, Star, User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTimezone, COMMON_TIMEZONES } from '../lib/timezone';
import { useAuth } from './TenantContext';
import { usePermission } from '../hooks/usePermission';
import { useAssistantStore, useTenantConversations } from '../store/assistantStore';
import { toggleAssistantPanel } from './assistantModes';
import { useAuthStore } from '../store/authStore';
import { useHumanActionsStore } from '../store/humanActionsStore';
import { useReleaseStore } from '../store/releaseStore';
import { usePermissionStore } from '../store/permissionStore';
import { useUiStore } from '../store/uiStore';
import { useAppFocusStore } from './appFocus';
import { useTourStore } from '../tour/tourStore';
import { openConnectOnboarding } from './connectOnboardingStore';
import { TOURS } from '../tour/steps';
import { openPurposesDialog } from '../modules/purposes/PurposesDialog';
import { navItemFor, permissionModuleFor, routeForPage, type NavGroupId } from './routes';
import { openAcademy } from '../modules/academy/academyRoute';
import { ResourceIcon } from '../design-system/resourceIcons';
import { useRecents, useFavorites, type ResourceRef } from './recents';
import { navigateToResource } from './useResourceRoute';
import './nav.css';

// Sidebar of the new interface (ui_v2): pages grouped by stage, the way data
// moves through the platform, with Home, Search, Files and the Inbox on top
// and a Help menu at the bottom. Tenants without ui_v2 keep NavRail.tsx.

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

// ── Nav items ──────────────────────────────────────────────────────────────

export interface NavItem {
  id: string;
  label: string;
  i18nKey: string;
  icon: React.ReactNode;
  active: boolean;
  comingSoon?: boolean;
  path: string;
  adminOnly?: boolean;
  superadminOnly?: boolean;
  alwaysVisible?: boolean;
}

// Order inside each group is the order shown. The group comes from
// shell/routes.ts, so a page and its URL prefix can never disagree.
export const NAV_ITEMS: NavItem[] = [
  { id: 'home',           label: 'Home',           i18nKey: 'nav.home',          icon: <Home size={16} />,              active: true, path: 'home', alwaysVisible: true },
  { id: 'files',          label: 'Files',          i18nKey: 'nav.files',         icon: <FolderOpen size={16} />,        active: true, path: 'files', alwaysVisible: true },
  { id: 'human-actions',  label: 'Inbox',          i18nKey: 'nav.inbox',         icon: <Inbox size={16} />,             active: true, path: 'human-actions', alwaysVisible: true },
  { id: 'connectors',     label: 'Connectors',     i18nKey: 'nav.connectors',    icon: <Plug size={16} />,              active: true, path: 'connectors' },
  { id: 'data',           label: 'Data',           i18nKey: 'nav.data',          icon: <Database size={16} />,          active: true, path: 'data' },
  { id: 'pipelines',      label: 'Pipelines',      i18nKey: 'nav.pipelines',     icon: <Workflow size={16} />,          active: true, path: 'pipelines' },
  { id: 'utilities',      label: 'Utilities',      i18nKey: 'nav.utilities',     icon: <Wrench size={16} />,            active: true, path: 'utilities' },
  { id: 'ontology',       label: 'Ontology',       i18nKey: 'nav.ontology',      icon: <Network size={16} />,           active: true, path: 'ontology' },
  { id: 'logic',          label: 'Logic Studio',   i18nKey: 'nav.logicStudio',   icon: <BrainCircuit size={16} />,      active: true, path: 'logic' },
  { id: 'agents',         label: 'Agent Studio',   i18nKey: 'nav.agentStudio',   icon: <Bot size={16} />,               active: true, path: 'agents' },
  { id: 'external-apps',  label: 'App Studio',     i18nKey: 'nav.appStudio',     icon: <Boxes size={16} />,             active: true, path: 'external-apps', adminOnly: true },
  { id: 'workbench',      label: 'Workbench',      i18nKey: 'nav.workbench',     icon: <BookOpen size={16} />,          active: true, path: 'workbench', alwaysVisible: true },
  { id: 'evals',          label: 'Evals',          i18nKey: 'nav.evals',         icon: <FlaskConical size={16} />,      active: true, path: 'evals' },
  { id: 'apps',           label: 'Dashboards',     i18nKey: 'nav.dashboards',    icon: <LayoutDashboard size={16} />,   active: true, path: 'apps' },
  { id: 'apps-app',       label: 'Apps',           i18nKey: 'nav.apps',          icon: <AppWindow size={16} />,         active: true, path: 'apps-app' },
  { id: 'operations',     label: 'Operations',     i18nKey: 'nav.operations',    icon: <Radio size={16} />,             active: true, path: 'operations', alwaysVisible: true },
  { id: 'process-mining', label: 'Process Mining', i18nKey: 'nav.processMining', icon: <ScanSearch size={16} />,        active: true, path: 'process-mining' },
  { id: 'insights',       label: 'Signals',        i18nKey: 'nav.signals',       icon: <Sparkles size={16} />,          active: true, path: 'insights' },
  { id: 'predictions',    label: 'Predictions',    i18nKey: 'nav.predictions',   icon: <Target size={16} />,            active: true, path: 'predictions' },
  { id: 'value',          label: 'Value Monitor',  i18nKey: 'nav.value',         icon: <TrendingUp size={16} />,        active: true, path: 'value' },
  { id: 'scenarios',      label: 'Scenarios',      i18nKey: 'nav.scenarios',     icon: <BarChart2 size={16} />,         active: true, path: 'scenarios' },
  { id: 'documents',      label: 'Documents',      i18nKey: 'nav.documents',     icon: <FileText size={16} />,          active: true, path: 'documents' },
  { id: 'discovery',      label: 'Discovery',      i18nKey: 'nav.discovery',     icon: <Compass size={16} />,           active: true, path: 'discovery' },
  { id: 'solution-design', label: 'Solution design', i18nKey: 'nav.solutionDesign', icon: <DraftingCompass size={16} />, active: true, path: 'solution-design' },
  { id: 'task-mining',    label: 'Task Mining',    i18nKey: 'nav.taskMining',    icon: <MousePointerClick size={16} />, active: true, path: 'task-mining', adminOnly: true },
  { id: 'admin',          label: 'Admin',          i18nKey: 'nav.admin',         icon: <Shield size={16} />,            active: true, path: 'admin', adminOnly: true },
  { id: 'settings',       label: 'Settings',       i18nKey: 'nav.settings',      icon: <Settings size={16} />,          active: true, path: 'settings', alwaysVisible: true },
  { id: 'activity',       label: 'Activity',       i18nKey: 'nav.activity',      icon: <Activity size={16} />,          active: true, path: 'activity' },
  { id: 'branches',       label: 'Branches',       i18nKey: 'nav.branches',      icon: <GitBranch size={16} />,         active: true, path: 'branches', alwaysVisible: true },
  { id: 'changes',        label: 'Changes',        i18nKey: 'nav.changes',       icon: <GitPullRequest size={16} />,    active: true, path: 'changes', alwaysVisible: true },
  { id: 'solutions',      label: 'Solutions',      i18nKey: 'nav.solutions',     icon: <Package size={16} />,           active: true, path: 'solutions', alwaysVisible: true },
  { id: 'platform',       label: 'Platform',       i18nKey: 'nav.platform',      icon: <Globe size={16} />,             active: true, path: 'platform', superadminOnly: true },
  { id: 'incidents',      label: 'Incidents',      i18nKey: 'nav.incidents',     icon: <Siren size={16} />,             active: true, path: 'incidents', superadminOnly: true },
];

const MAIC_SUBITEMS: NavItem[] = [
  { id: 'projects', label: 'Projects', i18nKey: 'nav.projects', icon: <Briefcase size={14} />, active: true, path: 'projects' },
  { id: 'finance',  label: 'Finance',  i18nKey: 'nav.finance',  icon: <DollarSign size={14} />, active: true, path: 'finance' },
];

// Groups shown with a header, in order. 'workspace' items sit on top with no
// header (Files and the Inbox are where every user starts).
export const GROUPS: { id: NavGroupId; i18nKey: string; label: string; icon: React.ReactNode }[] = [
  { id: 'connect', i18nKey: 'nav.groups.connect', label: 'Connect', icon: <Plug size={16} /> },
  { id: 'model',   i18nKey: 'nav.groups.model',   label: 'Model',   icon: <Network size={16} /> },
  { id: 'build',   i18nKey: 'nav.groups.build',   label: 'Build',   icon: <Layers size={16} /> },
  { id: 'operate', i18nKey: 'nav.groups.operate', label: 'Operate', icon: <LayoutDashboard size={16} /> },
  { id: 'analyze', i18nKey: 'nav.groups.analyze', label: 'Analyze', icon: <ScanSearch size={16} /> },
  { id: 'admin',   i18nKey: 'nav.groups.admin',   label: 'Admin',   icon: <Shield size={16} /> },
];

const groupOf = (item: NavItem): NavGroupId => routeForPage(item.path)?.group ?? 'workspace';

const COLLAPSED_GROUPS_KEY = 'nexus_nav_collapsed_groups';

// C1 (REDISENO_UI.md 3.1): groups start folded, except the one holding the
// current page. Once the user toggles any group the stored list wins.
const ALL_GROUP_IDS = ['connect', 'model', 'build', 'operate', 'analyze', 'admin'];
const MAX_PINNED = 8;
const MAX_RECENT_SHOWN = 10;

function readCollapsedGroups(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_GROUPS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : ALL_GROUP_IDS);
  } catch {
    return new Set(ALL_GROUP_IDS);
  }
}

function writeCollapsedGroups(groups: Set<string>) {
  try { localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify([...groups])); } catch { /* storage blocked */ }
}

// ── Component ──────────────────────────────────────────────────────────────

interface NavRailProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  additionalItems?: NavItem[];
}

export const GroupedNavRail: React.FC<NavRailProps> = ({ currentPage, onNavigate, additionalItems = [] }) => {
  const navCollapsed = useUiStore((s) => s.navCollapsed);
  const toggleUserNav = useUiStore((s) => s.toggleNav);
  // An app in focused mode folds the sidebar to icons without touching the
  // user's own preference; the chevron opens it for as long as the app stays.
  const appFocus = useAppFocusStore((s) => s.focused);
  const focusRailOpen = useAppFocusStore((s) => s.railOpen);
  const toggleFocusRail = useAppFocusStore((s) => s.toggleRail);
  const expanded = appFocus ? focusRailOpen : !navCollapsed;
  const toggleNav = appFocus ? toggleFocusRail : toggleUserNav;
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(readCollapsedGroups);
  const [maicExpanded, setMaicExpanded] = useState(true);
  const [maicFlyout, setMaicFlyout] = useState<{ top: number } | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const [tz, setTz] = useTimezone();
  const { canAdminister, isPlatformOperator, canAccess, modules, effective } = usePermission();
  const { t, i18n } = useTranslation();
  // The single assistant (C18): opens in the page's mode, ⌘J does the same.
  const assistantOpen = useAssistantStore((s) => s.open);
  const openCommandPalette = useUiStore((s) => s.openCommandPalette);
  const conversations = useTenantConversations();
  const isImpersonating = !!useAuthStore(s => s.user?.impersonated_by);
  // Each guided demo storyboard references apps/agents seeded only in its
  // demo tenant — the launcher shows exactly where a TOURS entry exists.
  const tourTenantId = useAuthStore(s => s.user?.tenant_id);
  const hasGuidedDemo = !!(tourTenantId && TOURS[tourTenantId]);
  const { pendingCount, fetchPending } = useHumanActionsStore();
  const pendingChanges = useReleaseStore(s => s.pendingApprovals);
  const fetchChangeRequests = useReleaseStore(s => s.fetchChangeRequests);
  const userId = useAuthStore(s => s.user?.id);
  const tourActive = useTourStore((s) => s.active);
  const pinned = useFavorites().slice(0, MAX_PINNED);
  const recents = useRecents(MAX_RECENT_SHOWN);
  const effectiveLoaded = usePermissionStore(s => s.loaded);
  const fetchEffective = usePermissionStore(s => s.fetchEffective);

  // Poll for pending actions every 30s
  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Poll change requests awaiting review every 60s (badge on "Changes").
  useEffect(() => {
    const poll = () => fetchChangeRequests('in_review');
    poll();
    const interval = setInterval(poll, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Resolve granular permissions for the signed-in user (drives nav gating).
  useEffect(() => {
    if (userId) fetchEffective();
  }, [userId, fetchEffective]);

  // Module access, not a role. The old `|| currentUser?.role === 'ADMIN'`
  // fallback to TenantContext is gone with it: two sources for one answer is
  // how the nav ended up disagreeing with the API.
  const isAdmin = canAdminister;

  const canSee = (moduleId: string): boolean => {
    if (isAdmin) return true;
    // Use JWT modules if available, otherwise fall back to TenantContext allowed_modules
    if (modules.length > 0) return modules.includes(moduleId);
    const mods = currentUser?.allowed_modules;
    if (!mods || mods.length === 0) return true;
    return mods.includes(moduleId);
  };

  const isVisible = (item: NavItem): boolean => {
    // Pages gated by another page's module (Process Mining by Activity) are
    // judged by that module; for every other item this is its own path.
    const moduleForPath = permissionModuleFor(item.path);
    // Constrained by a profile (effective doc present and not full):
    // show ONLY the modules the profile grants. This overrides
    // alwaysVisible / superadminOnly / adminOnly so a demoted user
    // (even a superadmin) sees exactly what their profile allows.
    if (effective && !effective.full) {
      // The non-gateable exemption lives in `moduleEnabled`, so every caller
      // gets it (see AppShell's redirect).
      return canAccess(moduleForPath) && item.active;
    }
    if (item.alwaysVisible) return item.active;
    // `platform` and `incidents`: cross-tenant scope AND the module
    // being granted, mirroring the backend, which demands both. Scope
    // alone would show the console to an operator whose profile does
    // not grant it; the module alone would show it to everyone while
    // Phase 2 is pending, since an unassigned user resolves to full.
    if (item.superadminOnly) {
      return isPlatformOperator && canAccess(moduleForPath) && item.active;
    }
    if (item.adminOnly && !canAccess(moduleForPath)) return false;
    if (effectiveLoaded) {
      // Granular permission doc is the source of truth once loaded.
      if (!canAccess(moduleForPath)) return false;
    } else if (modules.length > 0 && !canAccess(moduleForPath)) {
      return false;
    } else if (modules.length === 0 && !canSee(moduleForPath)) {
      // Legacy TenantContext module check when no JWT modules present
      return false;
    }
    return item.active;
  };

  const visibleItems = [...additionalItems, ...NAV_ITEMS.filter(item => !additionalItems.some(extra => extra.id === item.id))]
    .filter(isVisible);
  // C21: Administración and Configuración are one area, so Settings only
  // stays on the rail for a user who cannot see Admin (it is their way in).
  // Plataforma and Incidentes leave the groups and open from the account
  // menu, under the same gate they had here.
  const hasAdminItem = visibleItems.some(item => item.id === 'admin');
  const showPlatformEntry = visibleItems.some(item => item.id === 'platform');
  const items = visibleItems.filter(item =>
    item.id !== 'platform' && item.id !== 'incidents' && !(item.id === 'settings' && hasAdminItem));

  // A page without its own item (an open dashboard, a Data tab with its own
  // URL) highlights the item it belongs to.
  const activeItem = navItemFor(currentPage);
  const isActivePath = (path: string) =>
    currentPage === path || activeItem === path
    || (path === 'apps' && currentPage.startsWith('app-'))
    || (path === 'external-apps' && currentPage.startsWith('external-app:'));

  const badgeFor = (item: NavItem) =>
    item.id === 'human-actions' ? pendingCount : item.id === 'changes' ? pendingChanges : 0;

  const navBtn = (item: NavItem, isActive: boolean, onClick: () => void, indented = false) => {
    const badge = badgeFor(item);
    return (
      <button
        key={item.id}
        type="button"
        data-tour={`nav-${item.id}`}
        className={`nav-item${indented ? ' nav-item--indented' : ''}`}
        aria-current={isActive ? 'page' : undefined}
        onClick={onClick}
        title={!expanded ? t(item.i18nKey, item.label) : undefined}
      >
        <span className="nav-item__icon">{item.icon}</span>
        {expanded && <span className="nav-item__label">{t(item.i18nKey, item.label)}</span>}
        {badge > 0 && (
          <span className={`nav-item__badge${item.id === 'changes' ? ' nav-item__badge--info' : ''}`}>{badge}</span>
        )}
      </button>
    );
  };

  // A pinned or recent resource: opens at its own address, on its tab.
  const openRef = (ref: ResourceRef) => navigateToResource(ref.page, ref.id, { tab: ref.tab ?? null });
  const resourceBtn = (ref: ResourceRef, mark?: React.ReactNode) => (
    <button
      key={`${ref.kind}:${ref.id}`}
      type="button"
      className="nav-item nav-item--resource"
      data-testid="nav-resource"
      title={ref.name}
      onClick={() => openRef(ref)}
    >
      <span className="nav-item__icon"><ResourceIcon kind={ref.kind} size={14} colored={false} color={ref.color} /></span>
      <span className="nav-item__label">{ref.name}</span>
      {mark && <span className="nav-item__mark">{mark}</span>}
    </button>
  );

  const toggleGroup = (id: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      writeCollapsedGroups(next);
      return next;
    });
  };

  const topItems = items.filter(item => groupOf(item) === 'workspace');
  const showMaic = currentUser?.email?.endsWith('@maic.ai') && (canSee('projects') || canSee('finance'));
  const maicActive = currentPage === 'projects' || currentPage === 'finance';

  return (
    <nav className={`nav-rail${expanded ? '' : ' nav-rail--collapsed'}${isImpersonating ? ' nav-rail--impersonating' : ''}`} aria-label={t('nav.main', 'Main navigation')}>
      {/* Logo */}
      <div className="nav-rail__brand">
        {expanded ? (
          <>
            <MaicBrand size={22} className="nav-rail__logo" />
            <button type="button" className="nav-icon-btn" onClick={toggleNav} title={t('nav.collapseSidebar', 'Collapse sidebar')}>
              <ChevronLeft size={18} />
            </button>
          </>
        ) : (
          <button type="button" className="nav-icon-btn" onClick={toggleNav} title={t('nav.expandSidebar', 'Expand sidebar')}>
            <ChevronRight size={18} />
          </button>
        )}
      </div>

      {/* Nav items */}
      <div className="nav-rail__scroll">
        {topItems.slice(0, 1).map(item => navBtn(item, isActivePath(item.path), () => item.active && onNavigate(item.path)))}
        {/* One search: the same palette as Cmd+K (pages, records, resources). */}
        <button
          type="button"
          data-tour="nav-search"
          className="nav-item"
          onClick={openCommandPalette}
          title={!expanded ? t('nav.search', 'Search') : undefined}
        >
          <span className="nav-item__icon"><Search size={16} /></span>
          {expanded && <span className="nav-item__label">{t('nav.search', 'Search')}</span>}
          {expanded && <kbd className="nav-item__kbd">{navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl K'}</kbd>}
        </button>
        {topItems.slice(1).map(item => navBtn(item, isActivePath(item.path), () => item.active && onNavigate(item.path)))}

        {expanded && pinned.length > 0 && (
          <div className="nav-group" data-testid="nav-pinned">
            <div className="nav-group__header nav-group__header--static"><span>{t('nav.pinned', 'Pinned')}</span></div>
            {pinned.map(ref => resourceBtn(ref, <Star size={13} />))}
          </div>
        )}
        {expanded && recents.length > 0 && (
          <div className="nav-group" data-testid="nav-recents">
            <div className="nav-group__header nav-group__header--static"><span>{t('nav.recents', 'Recent')}</span></div>
            {recents.map(ref => resourceBtn(ref))}
          </div>
        )}

        {GROUPS.map(group => {
          const groupItems = items.filter(item => groupOf(item) === group.id);
          if (groupItems.length === 0) return null;
          const containsActive = groupItems.some(item => isActivePath(item.path));
          // A collapsed group still opens when it holds the page you are on,
          // so the active item is never hidden.
          // The guided tour points into every group, so it keeps them open.
          const open = !collapsedGroups.has(group.id) || containsActive || tourActive;
          return (
            <div key={group.id} className="nav-group" data-tour={`nav-group-${group.id}`}>
              {expanded ? (
                <button type="button" className="nav-group__header" aria-expanded={open} onClick={() => toggleGroup(group.id)}>
                  <span>{t(group.i18nKey, group.label)}</span>
                  <ChevronDown size={12} className="nav-group__chevron" />
                </button>
              ) : (
                <div className="nav-group__divider" />
              )}
              {(open || !expanded) && groupItems.map(item =>
                navBtn(item, isActivePath(item.path), () => item.active && onNavigate(item.path)),
              )}
            </div>
          );
        })}

        {/* ── maic group — only visible to @maic.ai users ─────────────────
              Projects and Finance are not permission modules (no catalog
              row), so a profile has nothing to say about them either way;
              the honest gate is the email domain. */}
        {showMaic && (
          <div className="nav-group">
            {expanded ? (
              <button type="button" className="nav-group__header" aria-expanded={maicExpanded || maicActive} onClick={() => setMaicExpanded(v => !v)}>
                <span>maic</span>
                <ChevronDown size={12} className="nav-group__chevron" />
              </button>
            ) : (
              <button
                type="button"
                className="nav-item"
                aria-current={maicActive ? 'page' : undefined}
                title="maic"
                onClick={(e) => { const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); setMaicFlyout(f => (f ? null : { top: r.top })); }}
              >
                <span className="nav-item__icon"><FolderKanban size={16} /></span>
              </button>
            )}
            {expanded && (maicExpanded || maicActive) && MAIC_SUBITEMS.filter(s => canSee(s.path)).map(sub =>
              navBtn(sub, currentPage === sub.path, () => onNavigate(sub.path)),
            )}
            {/* Collapsed: sub-items in a fixed flyout (escapes the nav's overflow:hidden) */}
            {!expanded && maicFlyout && (
              <>
                <div className="nav-backdrop" onClick={() => setMaicFlyout(null)} />
                <div className="nav-menu nav-menu--flyout" style={{ top: maicFlyout.top }}>
                  <div className="nav-menu__label">maic</div>
                  {MAIC_SUBITEMS.filter(s => canSee(s.path)).map(sub => (
                    <button
                      key={sub.id}
                      type="button"
                      className="nav-menu__item"
                      aria-current={currentPage === sub.path ? 'page' : undefined}
                      onClick={() => { onNavigate(sub.path); setMaicFlyout(null); }}
                    >
                      {sub.icon}
                      {t(sub.i18nKey)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Bottom: help, assistant, user */}
      <div className="nav-rail__footer">

        {/* Help menu — guided demo, academy and issue reports in one place */}
        {helpOpen && (
          <>
            <div className="nav-backdrop" onClick={() => setHelpOpen(false)} />
            <div className={`nav-menu ${expanded ? 'nav-menu--above' : 'nav-menu--flyout-bottom'}`}>
              {hasGuidedDemo && (
                <button type="button" className="nav-menu__item" onClick={() => { useTourStore.getState().start(); setHelpOpen(false); }}>
                  <Play size={13} /> {t('nav.demo', 'Guided demo')}
                </button>
              )}
              <button type="button" className="nav-menu__item" data-nav="connect-onboarding"
                      onClick={() => { openConnectOnboarding(); setHelpOpen(false); }}>
                <Plug size={13} /> {t('nav.connectData', 'Conectar datos')}
              </button>
              <button type="button" className="nav-menu__item" onClick={() => { onNavigate('academy'); setHelpOpen(false); }}>
                <GraduationCap size={13} /> {t('nav.academy', 'PAI Academy')}
              </button>
              {/* Learner community of Academy (GSL-1): /academy/community */}
              <button type="button" className="nav-menu__item" data-nav="academy-community"
                      onClick={() => { openAcademy({ view: 'community' }); setHelpOpen(false); }}>
                <MessagesSquare size={13} /> {t('nav.community', 'Academy community')}
              </button>
              <button type="button" className="nav-menu__item" onClick={() => { window.dispatchEvent(new CustomEvent('nexus:report-issue')); setHelpOpen(false); }}>
                <LifeBuoy size={13} /> {t('nav.reportIssue', 'Report an issue')}
              </button>
            </div>
          </>
        )}
        <button
          type="button"
          className="nav-item"
          aria-current={currentPage === 'academy' ? 'page' : undefined}
          aria-expanded={helpOpen}
          data-tour="nav-help"
          onClick={() => setHelpOpen(v => !v)}
          title={!expanded ? t('nav.help', 'Help') : undefined}
        >
          <span className="nav-item__icon"><HelpCircle size={16} /></span>
          {expanded && <span className="nav-item__label">{t('nav.help', 'Help')}</span>}
        </button>

        {/* Nexus Assistant button — violet is reserved for AI surfaces */}
        <button
          type="button"
          className={`nav-item nav-item--ai${assistantOpen ? ' is-open' : ''}`}
          onClick={toggleAssistantPanel}
          data-testid="nav-assistant"
          title={!expanded ? `${t('nav.assistant', 'Assistant')} (⌘J)` : undefined}
        >
          <span className="nav-item__icon"><MessageSquare size={16} /></span>
          {expanded && <span className="nav-item__label">{t('nav.assistant', 'Assistant')}</span>}
          {expanded && conversations.length > 0 && <span className="nav-item__badge nav-item__badge--ai">{conversations.length}</span>}
        </button>

        {/* User dropdown menu */}
        {userMenuOpen && currentUser && (
          <>
            <div className="nav-backdrop" onClick={() => setUserMenuOpen(false)} />
            <div className={`nav-menu ${expanded ? 'nav-menu--above' : 'nav-menu--flyout-bottom'}`}>
              <div className="nav-menu__section">
                <div className="nav-menu__label"><Globe size={11} /> {t('user.language')}</div>
                <div className="nav-menu__row">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      type="button"
                      className="nav-chip"
                      aria-pressed={i18n.language === lang.code}
                      onClick={() => i18n.changeLanguage(lang.code)}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Timezone selector — drives time-range filters and
                  timestamp display platform-wide. */}
              <div className="nav-menu__section">
                <div className="nav-menu__label"><Clock size={11} /> {t('user.timezone', 'Timezone')}</div>
                <select className="nav-select" value={tz} onChange={(e) => setTz(e.target.value)}>
                  {COMMON_TIMEZONES.map((zone) => (
                    <option key={zone.tz} value={zone.tz}>{zone.label}</option>
                  ))}
                </select>
              </div>

              {/* Cuenta and Plataforma (C21): what is personal, and the
                  superadmin area, outside the groups of the rail. */}
              <div className="nav-menu__section nav-menu__section--links">
                <button type="button" className="nav-menu__item" data-testid="open-account"
                  onClick={() => { setUserMenuOpen(false); onNavigate('account'); }}>
                  <User size={13} />
                  {t('nav.account', 'Cuenta')}
                </button>
                {showPlatformEntry && (
                  <button type="button" className="nav-menu__item" data-testid="open-platform"
                    onClick={() => { setUserMenuOpen(false); onNavigate('platform'); }}>
                    <Globe size={13} />
                    {t('nav.platform', 'Plataforma')}
                  </button>
                )}
              </div>

              {/* Propósitos de acceso (INTRO-6): activar uno con justificación. */}
              <div className="nav-menu__section nav-menu__section--links">
                <button type="button" className="nav-menu__item" data-testid="open-purposes"
                  onClick={() => { setUserMenuOpen(false); openPurposesDialog(); }}>
                  <KeyRound size={13} />
                  {t('user.purposes', 'Propósitos de acceso')}
                </button>
              </div>

              {/* Public policies. Same documents the sign-in screen links to,
                  reachable from inside a session — GDPR art. 13 transparency
                  and Meta's platform-review requirement both expect the
                  privacy policy and the deletion route to stay one click away.
                  Plain anchors in a new tab: these routes live outside the
                  auth gate, and opening them must not discard in-app work. */}
              <div className="nav-menu__section nav-menu__section--links">
                {[
                  { href: '/privacy', icon: <ShieldCheck size={13} />, label: t('legal.privacy') },
                  { href: '/terms', icon: <FileText size={13} />, label: t('legal.terms') },
                  { href: '/data-deletion', icon: <Trash2 size={13} />, label: t('legal.deletion') },
                ].map(link => (
                  <a key={link.href} className="nav-menu__item" href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => setUserMenuOpen(false)}>
                    {link.icon}
                    {link.label}
                  </a>
                ))}
              </div>

              <button type="button" className="nav-menu__item nav-menu__item--danger" onClick={() => { logout(); setUserMenuOpen(false); }}>
                <LogOut size={13} />
                {t('user.signOut')}
              </button>
            </div>
          </>
        )}

        {/* User row — clickable, opens dropdown */}
        {currentUser && (
          <button
            type="button"
            className="nav-user"
            aria-expanded={userMenuOpen}
            onClick={() => setUserMenuOpen(v => !v)}
            title={!expanded ? currentUser.name : undefined}
          >
            <span className="nav-user__avatar">
              {currentUser.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
            </span>
            {expanded && (
              <>
                <span className="nav-user__text">
                  <span className="nav-user__name">{currentUser.name}</span>
                  {isImpersonating
                    ? <span className="nav-user__impersonating">{t('user.impersonating', 'IMPERSONATING')}</span>
                    : <span className="nav-user__role">{currentUser.role}</span>}
                </span>
                <ChevronDown size={11} className="nav-user__chevron" />
              </>
            )}
          </button>
        )}
      </div>
    </nav>
  );
};

export default GroupedNavRail;
